import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess, logoutSuccess, resetStore, setLoading } from '../redux/slices/authSlice';
import {
  setIsForgotPassword,
  setIsFromRegister,
  resetModalState,
  setIsFromLogin,
  setModalOpen,
  setStep
} from '../redux/slices/modalSlice';
import { useLoginMutation } from '../redux/slices/apiSlice';

import MainLoader from '../components/loader/mainLoader';

import PATHS from './paths';
import { ACCESS_TOKEN, REFRESH_TOKEN, USER } from '../constants/cookieConstants';

import { BRImages } from '../assets/imgs';
import { mediaApiSlice } from '../redux/slices/mediaApi';
import { clearAllCookies, getCookies, setCookies } from '../redux/cookieHandler';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const { user, loading } = useSelector((state) => state.auth);

  const [loginMutation] = useLoginMutation();
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const openNotification = useCallback(
    ({ message, title, type }) => {

      if (notificationOpen) return;
      setNotificationOpen(true);

      const iconMap = {
        success: BRImages.CommonImages.SuccessIcon,
        error: BRImages.CommonImages.UnSuccessIcon,
        warning: BRImages.CommonImages.UnSuccessIcon,
        info: BRImages.CommonImages.UnSuccessIcon
      };

      const titleClassMap = {
        success: 'alert-title-green',
        error: 'alert-title-red',
        warning: 'alert-title-yellow',
        info: 'alert-title-blue'
      };

      const iconClassMap = {
        success: 'alert-icon-green',
        error: 'alert-icon-red',
        warning: 'alert-icon-yellow',
        info: 'alert-icon-blue'
      };

      api.info({
        description: <span className='alert-msg'>{message}</span>,
        message: (
          <span className={`alert-title ${titleClassMap[type] || 'alert-title-default'}`}>
            {title || type.charAt(0).toUpperCase() + type.slice(1) || 'Notification'}
          </span>
        ),
        icon: (
          <span className={`alert-icon ${iconClassMap[type] || 'alert-icon-default'}`}>
            {iconMap[type] ? (
              <img src={iconMap[type]} alt={`${type}-icon`} className='alert-icon' />
            ) : (
              <span className='alert-icon-placeholder' />
            )}
          </span>
        ),
        placement: 'bottomRight',
        duration: 2,
        onClose: () => {
          setNotificationOpen(false);
        }
      });
    },
    [api]
  );

  useEffect(() => {
    const updateNetworkStatus = () => {
      const onlineStatus = navigator.onLine;
      setIsOnline(onlineStatus);
      !onlineStatus &&
        openNotification({
          message: 'You are offline',
          title: 'Network Status',
          type: 'warning'
        });
    };

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);
    updateNetworkStatus();

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
    };
  }, [openNotification]);

  const login = useCallback(
    async (username, password) => {
      // dispatch(setLoading(true));
      if (!isOnline) {
        openNotification({
          message: 'Cannot login while offline. Please check your network connection.',
          title: 'Error',
          type: 'error'
        });
        return;
      }

      try {
        const loginReqParams = { emailOrUsername: username, password };
        const response = await loginMutation(loginReqParams).unwrap();
        const { data, message } = response;

        const loggedInUser = {
          displayName: data?.user?.displayName,
          name: data?.user?.username || username,
          email: data?.user?.email,
          id: data?.user?.userId,
          roleLevel: data?.user?.userType,
          isNewUser: data?.isNewUser || false,
          initiatedForgetPassword: false,
          privileges: data?.user?.privileges,
          userProfile: data?.user?.userProfile
        };

        dispatch(loginSuccess(loggedInUser));
        setCookies(USER, JSON.stringify(loggedInUser), { path: '/' });
        setCookies(ACCESS_TOKEN, data.accessToken, { path: '/' });
        setCookies(REFRESH_TOKEN, data.refreshToken, { path: '/' });

        setTimeout(() => {
          openNotification({ message: message, title: 'Success', type: 'success' });
        }, 0);

        if (loggedInUser.isNewUser || loggedInUser.initiatedForgetPassword) {
          dispatch(setIsForgotPassword(true));
          dispatch(setStep('setNewPassword'));
          dispatch(setIsFromLogin(!loggedInUser.isNewUser));
          dispatch(setIsFromRegister(loggedInUser.isNewUser));
          dispatch(setModalOpen(true));
        } else {
          navigate(PATHS.HOME);
        }
      } catch (error) {
        console.error('Login failed:', error);
        setTimeout(() => {
          openNotification({
            message: error?.data?.message || 'Login failed. Please check your credentials.',
            title: 'Error',
            type: 'error'
          });
        }, 0);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, navigate, loginMutation, openNotification, isOnline]
  );

  useEffect(() => {
    const storedUser = getCookies(USER);
    if (storedUser) {
      dispatch(loginSuccess(storedUser));
    } else {
      console.log('No stored user found. Logging in as guest.');
    }
    setIsLoading(false);
  }, [dispatch]);

  const logout = useCallback(() => {
    dispatch(setLoading(true));
    setTimeout(() => {
      dispatch(logoutSuccess());
      dispatch(resetStore());
      dispatch(resetModalState());
      clearAllCookies();
      setTimeout(() => {
        openNotification({
          message: 'Successfully logged out!',
          title: 'Success',
          type: 'success'
        });
      }, 0);
      dispatch(mediaApiSlice.util.invalidateTags(['Videos']));
      dispatch(mediaApiSlice.util.invalidateTags(['Audios']));
      dispatch(setLoading(false));
    }, 1000);
  }, [dispatch, openNotification]);

  const value = {
    user,
    loading: isLoading || loading,
    login,
    logout,
    isOnline,
    openNotification
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? (
        [children, contextHolder]
      ) : (
        <div className='fullscreen-loader fixed'>
          <MainLoader style={{ height: 250, width: 250 }} />
        </div>
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
